import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  viewChild,
  signal,
} from '@angular/core'

import {
  ApplicationMenuItem,
  ApplicationName,
  DeviceDetectorService,
  SafePipe,
} from '@performance/ui'
import { TranslateService, TranslatePipe } from '@ngx-translate/core'

import { DialogType } from './dialog-application.enums'
import { DownloadItem } from './dialog-application.models'
import { Modal } from '../modal/modal'
import { getHeader } from './dialog-application.utils'
import {
  IMAGE_BASE_PATH,
  INSTALLER_CONFIG_BY_APPLICATION,
  SHOW_OS_ICON_APPLICATIONS,
} from './dialog-application.constants'

import { DownloadService } from '@shared/services/download/download.service'
import { DefaultImageDirective } from '@shared/directives'

@Component({
  selector: 'bo-dialog-application',
  templateUrl: './dialog-application.html',
  styleUrls: ['./dialog-application.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Modal, DefaultImageDirective, TranslatePipe, SafePipe],
})
export class DialogApplication implements OnInit {
  readonly #destroyRef = inject(DestroyRef)
  readonly #downloadService = inject(DownloadService)
  readonly #translate = inject(TranslateService)
  readonly #deviceDetector = inject(DeviceDetectorService)

  readonly modal = viewChild<Modal>('modal')
  readonly type = signal<DialogType>(undefined)
  readonly application = signal<ApplicationMenuItem>(undefined)
  readonly userProfile = signal<any>({})
  readonly downloadList = signal<DownloadItem[]>(undefined)
  readonly headerImages = signal<any[]>([])
  dialogType = DialogType
  deviceOs = ''
  currentLang: string
  readonly notInstallationAllowed = signal<boolean>(false)

  IMAGE_BASE_PATH = IMAGE_BASE_PATH
  SHOW_OS_ICON_APPLICATIONS = SHOW_OS_ICON_APPLICATIONS

  private _getDownloadList(assetsDownload, applicationName: ApplicationName) {
    const installer = assetsDownload[`${applicationName}Installer`]
    this.currentLang = this.#translate.currentLang()
    return installer
      ? [
          ...INSTALLER_CONFIG_BY_APPLICATION[applicationName].map((item) => ({
            ...item,
            url: [
              ApplicationName.LivePro,
              ApplicationName.Mobile,
              ApplicationName.Desktop,
            ].includes(applicationName)
              ? (
                  (installer[this.currentLang] || []).find(
                    (appItem) => appItem.extension === item.extension,
                  ) || {}
                ).path
              : installer[this.currentLang][0].path,
          })),
        ]
      : []
  }

  ngOnInit() {
    this.deviceOs = this.#deviceDetector.getDeviceInfo().os
  }

  openModal(type: DialogType, application: ApplicationMenuItem, userProfile: any) {
    this.notInstallationAllowed.set(this._isInstallationForbidden(application.name))
    this.headerImages.set(getHeader(type, application.name))
    this.type.set(type)
    this.application.set(application)
    this.userProfile.set(userProfile)
    if (type === DialogType.Download) {
      this.#downloadService
        .getAssetsDownload$()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((assetsDownload) => {
          this.downloadList.set(
            this._getDownloadList(assetsDownload, application.name as ApplicationName),
          )
        })
    }
    this.modal().openModal()
  }

  closeModal() {
    this.modal().closeModal()
  }

  private _isInstallationForbidden(appName: string): boolean {
    switch (appName) {
      case ApplicationName.Mobile:
        return this.#deviceDetector.isTablet() || this.#deviceDetector.isDesktop()
      case ApplicationName.Desktop:
        return this.#deviceDetector.isTablet() || this.#deviceDetector.isMobile()
      case ApplicationName.LivePro:
        return this.#deviceDetector.isMobile() || this.#deviceDetector.isDesktop()
    }
    return false
  }
}
